import CustomComponent from "../../components/customComponent";
import { withToast } from "../../App";
import hoistStatics from "hoist-non-react-statics";
import { withTranslation } from "react-i18next";
import { withRouter } from "../../components/withRouter";
import { BoaterLayout } from "../../components/Layouts";
import PenEditIcon from "../../assets/img/icons/pen-edit.svg";
import React from "react";
import { CircularProgress } from "@mui/material";
import { StepNavigation } from "../../components/StepNavigation";
import { withBoaterStorage } from "../../hooks/useBoaterStorage";
import { API_URL } from "../../config";

const POLL_INTERVAL = 3000; // Poll every 3 seconds
const MAX_POLL_COUNT = 1200; // Maximum of 60 minutes (1200 * 3 seconds)

class BoaterProcessPayment extends CustomComponent {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            isIframeLoading: true,
            pollCount: 0,
            paymentVerified: false,
            paymentStatus: null, // 'success', 'error', or 'cancel'
        };

        this.pollTimer = null;
        this.iframeRef = React.createRef();
        this._isMounted = false;
        this.iframeObjectUrl = null;
    }

    componentDidMount() {
        this._isMounted = true;
        this.checkIsConnectedPWA();
        this.initiatePayment();
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
        if (this.iframeObjectUrl) {
            URL.revokeObjectURL(this.iframeObjectUrl);
            this.iframeObjectUrl = null;
        }
    }

    initiatePayment = async () => {
        this.setState({
            isIframeLoading: true,
            paymentStatus: null,
            paymentVerified: false,
        });

        if (this.iframeRef.current) {
            this.iframeRef.current.src = API_URL + "/paybox/payment/3ds/";
            this.iframeRef.current.onload = () => {
                this.setState({ isIframeLoading: false });
            };
        }

        this.startPollingPaymentStatus();
    };

    resetPayment = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }

        this.setState(
            {
                isLoading: false,
                isIframeLoading: true,
                pollCount: 0,
            },
            () => {
                if (this.iframeRef.current) {
                    this.iframeRef.current.src = "about:blank";
                }
                setTimeout(() => {
                    this.initiatePayment();
                }, 1000);
            },
        );
    };

    startPollingPaymentStatus = () => {
        this.pollTimer = setTimeout(this.checkPaymentStatus, POLL_INTERVAL);
    };

    checkPaymentStatus = async () => {
        if (this.state.pollCount >= MAX_POLL_COUNT) {
            // Stop polling after maximum attempts
            return;
        }

        try {
            const response = await this.request(
                "/paybox/payment/status",
                "GET",
            );

            if (response && response.status !== "null") {
                if (response.status === "success") {
                    this.handlePaymentSuccess();
                    return;
                } else if (response.status === "error") {
                    this.handlePaymentFailure();
                    return;
                } else if (response.status === "cancel") {
                    this.handlePaymentCancel();
                    return;
                }
            }
        } catch (error) {
            console.log("Error checking payment status:", error);
        }

        this.setState({ pollCount: this.state.pollCount + 1 });
        this.pollTimer = setTimeout(this.checkPaymentStatus, POLL_INTERVAL);
    };

    handlePaymentSuccess = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }

        this.setState(
            {
                paymentStatus: "success",
                isLoading: true,
            },
            async () => {
                try {
                    const response = await this.request(
                        "/paybox/payment/verify",
                        "GET",
                    );

                    if (response && response.status === "ok") {
                        this.setState({
                            isLoading: false,
                            paymentVerified: true,
                        });

                        this.props.addToast(
                            this.props.t("payment_success_message"),
                            {
                                appearance: "success",
                                autoDismiss: true,
                            },
                        );

                        this.props.updateBoaterStorage({
                            paymentAuthorized: true,
                        });
                        this.props.navigate("/boater/step4");
                    } else {
                        this.setState({
                            isLoading: false,
                            paymentVerified: false,
                        });

                        this.props.addToast(
                            this.props.t("payment_error_message"),
                            {
                                appearance: "error",
                                autoDismiss: true,
                            },
                        );

                        this.resetPayment();
                    }
                } catch {
                    this.setState({
                        isLoading: false,
                        paymentVerified: false,
                    });

                    this.props.addToast(this.props.t("payment_error_message"), {
                        appearance: "error",
                        autoDismiss: true,
                    });

                    this.resetPayment();
                }
            },
        );
    };

    handlePaymentFailure = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }

        this.props.addToast(this.props.t("payment_error_message"), {
            appearance: "error",
            autoDismiss: true,
        });

        this.resetPayment();
    };

    handlePaymentCancel = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }

        this.props.addToast(this.props.t("payment_cancel_message"), {
            appearance: "warning",
            autoDismiss: true,
        });

        this.resetPayment();
    };

    handlePrevious = () => {
        this.props.navigate("/boater/step2");
    };

    render() {
        const { t } = this.props;
        const { isLoading } = this.state;

        return (
            <BoaterLayout step={3} loading={isLoading}>
                <div className="boater__step-container">
                    <div className="boater-payment__payment-details">
                        <div className="boater-payment__header">
                            <img src={PenEditIcon} width="60" alt="" />
                            <h2>{t("boater_process_bank_info")}</h2>
                        </div>
                        <div className="boater-payment__content">
                            <div className="payment-iframe-container">
                                <div className="content-loading-overlay">
                                    <div className="content-loading-overlay__content">
                                        <div className="content-loading-overlay__spinner">
                                            <CircularProgress color="inherit" />
                                        </div>
                                        <div className="content-loading-overlay__text">
                                            {t("loading")}
                                        </div>
                                    </div>
                                </div>
                                <iframe
                                    ref={this.iframeRef}
                                    title="Payment"
                                    className="payment-iframe"
                                    sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation"
                                />
                            </div>
                        </div>
                    </div>
                    <StepNavigation
                        prevTo={"/boater/step2"}
                        prevTitle={t("Previous")}
                        prevHandler={this.handlePrevious}
                        nextHide
                    />
                </div>
            </BoaterLayout>
        );
    }
}
export default withToast(
    hoistStatics(
        withTranslation()(withRouter(withBoaterStorage(BoaterProcessPayment))),
        BoaterProcessPayment,
    ),
);
